/**
 * SiteUsersCard — on the cloud page, one site at a time: also email the users granted this site
 * in Site access when an OLDER site's system alerts go out (a current site resolves its own
 * people). Admins, who see every site, are not added: a site's alerts reach that site's people.
 */
import { useState } from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import { Callout } from '../common/Callout';
import { AACard, AASwitch } from './AlertActionsControls';
import { alertActionsErrorText, type SiteUser } from './alertActionsModel';


function useSaver() {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setSaving(true);
    setResult(null);
    try {
      await fn();
      setResult({ tone: 'success', text: 'Saved' });
    } catch (err) {
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Could not save') });
    } finally {
      setSaving(false);
    }
  };
  return { saving, result, run };
}


export function SiteUsersCard({
  value,
  users,
  onSave,
  canEdit,
}: {
  value: boolean | null;
  /** The users it would reach; null while loading. */
  users: SiteUser[] | null;
  onSave: (next: boolean) => Promise<unknown>;
  canEdit: boolean;
}) {
  const { saving, result, run } = useSaver();
  return (
    <AACard
      icon={UsersIcon}
      title="Also email the users who have access to this site"
      subtitle="Its system alerts also go to every active user granted this site in Site access. Admins see every site and are not added."
      right={
        <AASwitch
          checked={!!value}
          disabled={!canEdit || value === null}
          busy={saving}
          label="Also email the site's users"
          onChange={(v) => run(() => onSave(v))}
        />
      }
    >
      {users === null ? (
        <div className="h-8 animate-pulse rounded-lg bg-muted" />
      ) : users.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">Nobody has been granted this site yet (User management → Site access).</p>
      ) : (
        <div className={`flex flex-wrap gap-1.5 ${value ? '' : 'opacity-60'}`}>
          {users.map((u) => (
            <span key={u.id} title={u.email} className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[12px] text-foreground">
              {u.name || u.email}
            </span>
          ))}
        </div>
      )}
      {result?.tone === 'danger' && <Callout tone="danger" className="mt-2">{result.text}</Callout>}
    </AACard>
  );
}
